import { useCallback } from 'react';
import { useUser } from '../contexts/UserContext';

// Define user roles
export type UserRole = 
  | 'admin'
  | 'organization_admin'
  | 'project_manager'
  | 'scheduler'
  | 'field_engineer'
  | 'subcontractor'
  | 'cost_controller'
  | 'report_viewer'
  | 'user';

// Role display names
export const ROLE_DISPLAY_NAMES: Record<UserRole, string> = {
  admin: 'System Administrator',
  organization_admin: 'Organization Administrator',
  project_manager: 'Project Manager',
  scheduler: 'Scheduler',
  field_engineer: 'Field Engineer',
  subcontractor: 'Subcontractor',
  cost_controller: 'Cost Controller',
  report_viewer: 'Report Viewer',
  user: 'User',
};

// Role descriptions
export const ROLE_DESCRIPTIONS: Record<UserRole, string> = {
  admin: 'Full system access with user and organization management capabilities',
  organization_admin: 'Organization-level management with user and project oversight',
  project_manager: 'Project oversight with team management and reporting capabilities',
  scheduler: 'Schedule creation, optimization, and management',
  field_engineer: 'Field updates, task management, and progress tracking',
  subcontractor: 'Work package management and document submission',
  cost_controller: 'Budget tracking, cost analysis, and financial reporting',
  report_viewer: 'Access to Reports and Activities only',
  user: 'Basic project view access',
};

/**
 * Custom hook for managing user role state
 */
const useUserRole = () => {
  const { user, updateProfile } = useUser();
  
  // Get current role
  const role = user?.role as UserRole;
  
  // Set user role
  const setRole = useCallback((newRole: UserRole) => {
    if (user) {
      updateProfile({ ...user, role: newRole });
    }
  }, [user, updateProfile]);
  
  // Get role display name
  const getRoleDisplayName = useCallback((roleKey: UserRole): string => {
    return ROLE_DISPLAY_NAMES[roleKey] || roleKey;
  }, []);
  
  // Get role description
  const getRoleDescription = useCallback((roleKey: UserRole): string => {
    return ROLE_DESCRIPTIONS[roleKey] || '';
  }, []);
  
  // Check if user has a specific role
  const hasRole = useCallback((roleToCheck: UserRole | UserRole[]): boolean => {
    if (!role) return false;
    
    if (Array.isArray(roleToCheck)) {
      return roleToCheck.includes(role);
    }
    
    return role === roleToCheck;
  }, [role]);
  
  return {
    role,
    setRole,
    getRoleDisplayName,
    getRoleDescription,
    hasRole
  };
};

export default useUserRole;
